import React, { useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { Link, useInRouterContext } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { mediaUrl } from '../../utils/mediaUrl';
import { initialsFor, PrintContext, profileUrlFor } from './programModel';
import {
  Card, CardAction, CardActions, CardName, CardText, CardTop, CloseButton, Initials, Institution, NameButton,
  NameWrap, Note, Photo, PrintName, ProfileLink, Sep, SheetBackdrop,
} from './programStyles';

// A person's photo, or their initials when there is none (or it does not load). Round, as on the
// crew page. Decoration only: the name is always printed next to it.
export const Avatar = ({ name, photoUrl, size = 64 }) => {
  const [failed, setFailed] = useState(false);
  const printing = useContext(PrintContext);
  if (photoUrl && !failed) {
    return (
      <Photo
        $size={size}
        src={mediaUrl(photoUrl)}
        alt=""
        loading={printing ? 'eager' : 'lazy'}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return <Initials $size={size} aria-hidden="true">{initialsFor(name)}</Initials>;
};

// The link to the person's public profile: a router link inside the app, a plain link elsewhere
// (tests, the print window).
const Profile = ({ to, children }) => {
  const inRouter = useInRouterContext();
  return inRouter ? <ProfileLink as={Link} to={to}>{children}</ProfileLink> : <ProfileLink href={to}>{children}</ProfileLink>;
};

// Phones show the card as a sheet at the bottom of the screen (programStyles.Card). It is rendered at
// the end of <body>: inside the timeline, the slots' fade-in would make the slot, not the screen, the
// frame of a fixed element.
const SHEET_QUERY = '(max-width: 600px)';
const isPhone = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia(SHEET_QUERY).matches;

// A named person in the timeline: a pill with portrait and name, institution and display note after
// it, and a card with a larger photo on hover, keyboard focus or tap (decision 3 of §7.6). On phones
// the card is a sheet at the bottom of the screen. Escape, a tap elsewhere or the close button close it.
// The card links to the person's public profile once that page exists (programModel.PUBLIC_PROFILES).
// print: the name and institution as plain text (the admin's PDF export), no card.
const PersonName = ({ person, highlight, onShowEntries, print = false }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const wrapRef = useRef(null);
  const cardRef = useRef(null);
  const [shift, setShift] = useState(0);
  const cardId = useId();
  const profileUrl = profileUrlFor(person.personId);

  // Keep the card inside the viewport: shift it left when it would run off the right edge (a name
  // near the edge), right when off the left edge. Not needed on phones, where it is a fixed sheet.
  useLayoutEffect(() => {
    if (!open) { setShift(0); return; }
    const card = cardRef.current;
    if (!card || typeof window === 'undefined') return;
    if (window.getComputedStyle(card).position === 'fixed') return;
    const rect = card.getBoundingClientRect();
    const margin = 8;
    const width = window.innerWidth || document.documentElement.clientWidth;
    if (rect.right > width - margin) setShift(width - margin - rect.right);
    else if (rect.left < margin) setShift(margin - rect.left);
  }, [open]);

  useEffect(() => {
    if (!pinned) return undefined;
    // A tapped card closes when the visitor taps elsewhere.
    const onDown = (e) => {
      const inside = (wrapRef.current && wrapRef.current.contains(e.target))
        || (cardRef.current && cardRef.current.contains(e.target));
      if (!inside) {
        setPinned(false);
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [pinned]);

  const close = () => {
    setOpen(false);
    setPinned(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      close();
    }
  };

  const onBlur = (e) => {
    const inside = (wrapRef.current && wrapRef.current.contains(e.relatedTarget))
      || (cardRef.current && cardRef.current.contains(e.relatedTarget));
    if (!inside) close();
  };

  const after = (
    <>
      {person.institution && <Institution><Sep>, </Sep>{person.institution}</Institution>}
      {person.displayNote && <Note>{` ${person.displayNote}`}</Note>}
    </>
  );

  if (print) {
    return (
      <span>
        <PrintName>
          <Avatar name={person.name} photoUrl={person.photoUrl} size={20} />
          <strong>{person.name}</strong>
        </PrintName>
        {after}
      </span>
    );
  }

  const sheet = open && isPhone();
  const card = (
    <>
      {pinned && <SheetBackdrop aria-hidden="true" onMouseDown={close} onTouchStart={close} />}
      <Card
        ref={cardRef}
        id={cardId}
        role="dialog"
        aria-label={person.name}
        style={shift ? { transform: `translateX(${shift}px)` } : undefined}
      >
        <CloseButton
          type="button"
          aria-label={intl.formatMessage({ id: 'program.public.person.close', defaultMessage: 'Close' })}
          onClick={close}
        >
          ×
        </CloseButton>
        <CardTop>
          <Avatar name={person.name} photoUrl={person.photoUrl} size={72} />
          <CardText>
            <CardName>{person.name}</CardName>
            {person.institution && <Institution>{person.institution}</Institution>}
          </CardText>
        </CardTop>
        {(profileUrl || onShowEntries) && (
          <CardActions>
            {profileUrl && (
              <Profile to={profileUrl}>
                {intl.formatMessage({ id: 'program.public.person.profile', defaultMessage: 'Profile' })}
                <ArrowRight size={15} aria-hidden="true" />
              </Profile>
            )}
            {onShowEntries && (
              <CardAction type="button" onClick={() => { close(); onShowEntries(); }}>
                {intl.formatMessage({ id: 'program.public.person.showEntries', defaultMessage: 'All appearances' })}
              </CardAction>
            )}
          </CardActions>
        )}
      </Card>
    </>
  );

  return (
    <NameWrap
      ref={wrapRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => { if (!pinned) setOpen(false); }}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    >
      <NameButton
        type="button"
        $highlight={highlight}
        aria-expanded={open}
        aria-controls={open ? cardId : undefined}
        onFocus={() => setOpen(true)}
        onClick={() => {
          if (pinned) close();
          else { setPinned(true); setOpen(true); }
        }}
      >
        <Avatar name={person.name} photoUrl={person.photoUrl} size={28} />
        {person.name}
      </NameButton>
      <span data-after>{after}</span>
      {open && (sheet ? createPortal(card, document.body) : card)}
    </NameWrap>
  );
};

export default PersonName;
